import React, { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLibrary = () => {
  const [authed, setAuthed] = useState(false);
  const [creds, setCreds] = useState({ user: '', pass: '' });
  const [authError, setAuthError] = useState('');
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ case_id: '', is_returning: '', ck_synced: '', date_from: '', date_to: '' });
  const [authHeader, setAuthHeader] = useState('');

  const makeAuth = (u, p) => 'Basic ' + btoa(`${u}:${p}`);

  const handleLogin = async (e) => {
    e.preventDefault();
    const header = makeAuth(creds.user, creds.pass);
    try {
      const res = await fetch(`${API}/admin/library/stats`, { headers: { Authorization: header } });
      if (res.ok) {
        setAuthHeader(header);
        setAuthed(true);
        setAuthError('');
      } else {
        setAuthError('Invalid credentials.');
      }
    } catch {
      setAuthError('Connection error.');
    }
  };

  const fetchStats = useCallback(async () => {
    if (!authHeader) return;
    const res = await fetch(`${API}/admin/library/stats`, { headers: { Authorization: authHeader } });
    if (res.ok) setStats(await res.json());
  }, [authHeader]);

  const fetchSubmissions = useCallback(async () => {
    if (!authHeader) return;
    const params = new URLSearchParams();
    if (filters.case_id) params.set('case_id', filters.case_id);
    if (filters.is_returning === 'true') params.set('is_returning', 'true');
    if (filters.is_returning === 'false') params.set('is_returning', 'false');
    if (filters.ck_synced === 'true') params.set('ck_synced', 'true');
    if (filters.ck_synced === 'false') params.set('ck_synced', 'false');
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    const res = await fetch(`${API}/admin/library/submissions?${params}`, { headers: { Authorization: authHeader } });
    if (res.ok) {
      const data = await res.json();
      setSubmissions(data.submissions);
      setTotal(data.total);
    }
  }, [authHeader, filters]);

  useEffect(() => { if (authed) { fetchStats(); fetchSubmissions(); } }, [authed, fetchStats, fetchSubmissions]);

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace" }}>
        <form onSubmit={handleLogin} style={{ background: '#fff', border: '1px solid #e4e4e7', padding: '3rem', maxWidth: '360px', width: '100%' }}>
          <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#999', marginBottom: '1.5rem' }}>SHOW'YN Admin</p>
          <div style={{ marginBottom: '1rem' }}>
            <input type="text" placeholder="Username" value={creds.user} onChange={e => setCreds(p => ({ ...p, user: e.target.value }))} style={{ width: '100%', padding: '0.75rem 0', border: 'none', borderBottom: '1px solid #ddd', fontSize: '14px', outline: 'none', fontFamily: 'inherit' }} />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <input type="password" placeholder="Password" value={creds.pass} onChange={e => setCreds(p => ({ ...p, pass: e.target.value }))} style={{ width: '100%', padding: '0.75rem 0', border: 'none', borderBottom: '1px solid #ddd', fontSize: '14px', outline: 'none', fontFamily: 'inherit' }} />
          </div>
          {authError && <p style={{ color: '#D00000', fontSize: '12px', marginBottom: '1rem' }}>{authError}</p>}
          <button type="submit" style={{ width: '100%', padding: '0.75rem', background: '#000', color: '#fff', border: 'none', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer' }}>Access</button>
        </form>
      </div>
    );
  }

  const StatCard = ({ label, value }) => (
    <div style={{ background: '#fff', border: '1px solid #e4e4e7', padding: '1.5rem 2rem' }}>
      <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#999', marginBottom: '0.5rem' }}>{label}</p>
      <p style={{ fontSize: '2rem', fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{value ?? '—'}</p>
    </div>
  );

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa', fontFamily: "'JetBrains Mono', monospace" }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid #e4e4e7', padding: '1.25rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
        <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.25em', color: '#666' }}>SHOW'YN Business Library — Admin</p>
        <button onClick={() => { setAuthed(false); setAuthHeader(''); }} style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#999', cursor: 'pointer', background: 'none', border: 'none' }}>Logout</button>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
          <StatCard label="Total Submissions" value={stats?.total_submissions} />
          <StatCard label="Unique Emails" value={stats?.unique_emails} />
          <StatCard label="Returning Users" value={stats?.returning_users} />
          <StatCard label="New Users" value={stats?.new_users} />
          <StatCard label="Case 001 Accesses" value={stats?.case_001_accesses} />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center' }}>
          <select value={filters.case_id} onChange={e => setFilters(p => ({ ...p, case_id: e.target.value }))} style={filterStyle}>
            <option value="">All Cases</option>
            <option value="001">Legacy — Case 001</option>
            <option value="RB-001">Redesign Business — 001</option>
            <option value="BAI-001">Architecture — VENYOU</option>
            <option value="BAI-002">Architecture — CLEANER WORKFORCE</option>
          </select>
          <select value={filters.is_returning} onChange={e => setFilters(p => ({ ...p, is_returning: e.target.value }))} style={filterStyle}>
            <option value="">All Users</option>
            <option value="false">New</option>
            <option value="true">Returning</option>
          </select>
          <select value={filters.ck_synced} onChange={e => setFilters(p => ({ ...p, ck_synced: e.target.value }))} style={filterStyle}>
            <option value="">Kit Status: All</option>
            <option value="true">Synced</option>
            <option value="false">Not Synced</option>
          </select>
          <input type="date" value={filters.date_from} onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))} style={filterStyle} placeholder="From" />
          <input type="date" value={filters.date_to} onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))} style={filterStyle} placeholder="To" />
          <button onClick={() => setFilters({ case_id: '', is_returning: '', ck_synced: '', date_from: '', date_to: '' })} style={{ ...filterStyle, cursor: 'pointer', color: '#D00000' }}>Clear</button>
        </div>

        {/* Results count */}
        <p style={{ fontSize: '11px', color: '#999', marginBottom: '1rem' }}>{total} result{total !== 1 ? 's' : ''}</p>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: '#fff', border: '1px solid #e4e4e7' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e4e4e7', background: '#fafafa' }}>
                {['Date', 'Name', 'Email', 'Redesign Answer', 'Case', 'Kit', 'User'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#999', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {submissions.map((s, i) => (
                <tr key={s.id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={cellStyle}>{fmtDate(s.submitted_at)}</td>
                  <td style={cellStyle}>{s.first_name}</td>
                  <td style={{ ...cellStyle, color: '#333' }}>{s.email}</td>
                  <td style={{ ...cellStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.business_to_redesign_next || '—'}</td>
                  <td style={cellStyle}>{s.case_id}</td>
                  <td style={cellStyle}>
                    <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: s.convertkit_synced ? '#22c55e' : '#ef4444', marginRight: '6px' }} />
                    {s.convertkit_synced ? 'Synced' : 'Failed'}
                  </td>
                  <td style={cellStyle}>
                    {s.is_returning_user ? (
                      <span style={{ color: '#D00000', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Returning</span>
                    ) : (
                      <span style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>New</span>
                    )}
                  </td>
                </tr>
              ))}
              {submissions.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>No submissions found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const filterStyle = { padding: '0.5rem 0.75rem', border: '1px solid #e4e4e7', fontSize: '11px', background: '#fff', outline: 'none', fontFamily: 'inherit' };
const cellStyle = { padding: '0.75rem 1rem', color: '#555' };

export default AdminLibrary;
